import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, FormControlLabel, Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ArrowRightNoBGSVG, Close, Delete, Sign } from '@/components/Icon';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { Flex } from '@/layout';
import { validateSignatureV2Roster } from '@/pages/SignatureV2/workflowState';
import { theme } from '@/theme/Theme';
import type { SignatureV2ParticipantInput, SignatureV2Side } from '@/types/signatureV2';

import type { SignatureV2SignerCandidate } from '../../types';
import AddMoreSignersModal from '../AddMoreSingers';
import {
  ErrorText,
  LinkButton,
  ListData,
  ModalActions,
  ModalForm,
  ModalTitle,
  OrderButton,
  OrderList,
  ScrollArea,
  TextUnderline,
} from './styles';
import { moveSigner } from './order';

interface ModalProps {
  showModal: boolean;
  side: SignatureV2Side;
  companyName: string;
  candidates: SignatureV2SignerCandidate[];
  initialSigners: SignatureV2ParticipantInput[];
  otherPartyEmails: string[];
  busy: boolean;
  apiError: string;
  setShowModal: (show: boolean) => void;
  onSubmit: (signers: SignatureV2ParticipantInput[]) => Promise<void>;
}

const AddSignersModal = ({
  showModal,
  side,
  companyName,
  candidates,
  initialSigners,
  otherPartyEmails,
  busy,
  apiError,
  setShowModal,
  onSubmit,
}: ModalProps) => {
  // @ts-ignore i18next's deeply nested translation key type exceeds CRA's incremental compiler limit.
  const { t: typedT } = useTranslation();
  const t = typedT as unknown as (key: string, options?: Record<string, unknown>) => string;
  const [selectedSigners, setSelectedSigners] = useState<SignatureV2ParticipantInput[]>([]);
  const [showAddMoreSigners, setShowAddMoreSigners] = useState(false);
  const [editingExternalIndex, setEditingExternalIndex] = useState<number | null>(null);
  const [validationError, setValidationError] = useState('');
  const submitting = useRef(false);

  const availableCandidates = useMemo(() => {
    const byId = new Map<number, SignatureV2SignerCandidate>();
    candidates.forEach((candidate) => byId.set(candidate.user_id, candidate));
    initialSigners.forEach((signer) => {
      if (!signer.user_id || byId.has(signer.user_id)) return;
      byId.set(signer.user_id, {
        user_id: signer.user_id,
        name: signer.name,
        email: signer.email,
      });
    });
    return Array.from(byId.values());
  }, [candidates, initialSigners]);

  useEffect(() => {
    if (!showModal) {
      setShowAddMoreSigners(false);
      setEditingExternalIndex(null);
      return;
    }
    setSelectedSigners(initialSigners);
    setEditingExternalIndex(null);
    setValidationError('');
  }, [initialSigners, showModal]);

  const switchCandidate = (candidate: SignatureV2SignerCandidate) => {
    setSelectedSigners((current) => current.some((signer) => signer.user_id === candidate.user_id)
      ? current.filter((signer) => signer.user_id !== candidate.user_id)
      : [...current, candidate]);
  };

  const saveExternalSigner = (signer: SignatureV2ParticipantInput) => {
    setSelectedSigners((current) => editingExternalIndex === null
      ? [...current, signer]
      : current.map((item, index) => index === editingExternalIndex ? signer : item));
    setEditingExternalIndex(null);
  };

  const submit = async () => {
    if (busy || submitting.current) return;
    const error = validateSignatureV2Roster(selectedSigners, otherPartyEmails);
    if (error) {
      setValidationError(t(`signatureV2.validation.${error}`));
      return;
    }
    setValidationError('');
    submitting.current = true;
    try {
      await onSubmit(selectedSigners);
    } finally {
      submitting.current = false;
    }
  };

  return (
    <>
      <Modal open={showModal && !showAddMoreSigners} onClose={() => !busy && setShowModal(false)} disableEnforceFocus>
        <ModalForm
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          aria-labelledby="add-signers-title"
        >
          <Close.ButtonClose type="button" disabled={busy} onClick={() => setShowModal(false)}>
            <Close.CloseSVG />
          </Close.ButtonClose>

          <ScrollArea>
            <Flex flexDirection="column" gridGap={20}>
              <ModalTitle id="add-signers-title">{t('signatureV2.contract.addSignersTitle')}</ModalTitle>
              <Flex width="100%" gridGap={50}>
                <Text fontWeight={900} color={theme.colors.primary500}>
                  {t(`signatureV2.side.${side}`)}
                </Text>
                <TextUnderline>{companyName || '-'}</TextUnderline>
              </Flex>

              <Flex flexDirection="column" gridGap={12}>
                <Text fontWeight={900} color={theme.colors.primary500}>
                  {t('signatureV2.contract.membersInDeal')}
                </Text>
                <ListData>
                  {availableCandidates.length === 0 && <li className="empty">{t('signatureV2.contract.noMembers')}</li>}
                  {availableCandidates.map((candidate) => (
                    <li key={candidate.user_id}>
                      <div>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              disabled={busy}
                              sx={{ '&.Mui-checked': { color: '#7EB442' } }}
                              checked={selectedSigners.some((signer) => signer.user_id === candidate.user_id)}
                              onChange={() => switchCandidate(candidate)}
                            />
                          )}
                          label={candidate.name}
                        />
                      </div>
                      <div>{candidate.email}</div>
                    </li>
                  ))}
                </ListData>
              </Flex>

              <Text fontWeight={900} color={theme.colors.primary500}>
                {t('signatureV2.contract.addMoreSigners')}
              </Text>
              <ListData>
                {selectedSigners.map((signer, index) => !signer.user_id && (
                  <li key={`${signer.email}-${index}`}>
                    <div>
                      <FormControlLabel
                        control={<Checkbox sx={{ '&.Mui-checked': { color: '#7EB442' } }} checked disabled />}
                        label={signer.name}
                      />
                    </div>
                    <div>{signer.email}</div>
                    <div className="action">
                      <button
                        type="button"
                        aria-label={t('signatureV2.contract.editSigner')}
                        disabled={busy}
                        onClick={() => {
                          setEditingExternalIndex(index);
                          setShowAddMoreSigners(true);
                        }}
                      >
                        <Sign.SignSVG fill={theme.colors.primary500} />
                      </button>
                      <button
                        type="button"
                        aria-label={t('signatureV2.actions.removeSigner')}
                        disabled={busy}
                        onClick={() => setSelectedSigners((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        <Delete.DeleteSVG fill={theme.colors.primary500} />
                      </button>
                    </div>
                  </li>
                ))}
              </ListData>
              <LinkButton
                type="button"
                disabled={busy}
                onClick={() => {
                  setEditingExternalIndex(null);
                  setShowAddMoreSigners(true);
                }}
              >
                + {t('signatureV2.actions.addSigner')}
              </LinkButton>

              {selectedSigners.length > 0 && (
                <Flex flexDirection="column" gridGap={12}>
                  <Text fontWeight={900} color={theme.colors.primary500}>
                    {t('signatureV2.contract.signingOrder')}
                  </Text>
                  <OrderList>
                    {selectedSigners.map((signer, index) => (
                      <li key={signer.user_id ? `user-${signer.user_id}` : `external-${index}`}>
                        <span>{index + 1}</span>
                        <span title={signer.email}>{signer.name}</span>
                        <div>
                          <OrderButton
                            type="button"
                            $direction="up"
                            title={t('signatureV2.contract.moveSignerUp')}
                            aria-label={t('signatureV2.contract.moveSignerUpFor', { name: signer.name })}
                            disabled={index === 0 || busy}
                            onClick={() => setSelectedSigners((current) => moveSigner(current, index, -1))}
                          >
                            <ArrowRightNoBGSVG />
                          </OrderButton>
                          <OrderButton
                            type="button"
                            $direction="down"
                            title={t('signatureV2.contract.moveSignerDown')}
                            aria-label={t('signatureV2.contract.moveSignerDownFor', { name: signer.name })}
                            disabled={index === selectedSigners.length - 1 || busy}
                            onClick={() => setSelectedSigners((current) => moveSigner(current, index, 1))}
                          >
                            <ArrowRightNoBGSVG />
                          </OrderButton>
                        </div>
                      </li>
                    ))}
                  </OrderList>
                </Flex>
              )}

              {(validationError || apiError) && <ErrorText role="alert">{validationError || apiError}</ErrorText>}
            </Flex>

            <ModalActions>
              <Button type="submit" size="lg" variant="primary" rounded={false} disabled={busy}>
                {t('buttons.ok')}
              </Button>
              <Button
                type="button"
                size="lg"
                variant="primary-invert"
                color="primary"
                disabled={busy}
                onClick={() => setShowModal(false)}
                style={{ textDecoration: 'underline' }}
              >
                {t('buttons.cancel')}
              </Button>
            </ModalActions>
          </ScrollArea>
        </ModalForm>
      </Modal>

      <AddMoreSignersModal
        showModal={showModal && showAddMoreSigners}
        initialSigner={editingExternalIndex === null ? null : selectedSigners[editingExternalIndex]}
        setShowModal={setShowAddMoreSigners}
        onSave={saveExternalSigner}
      />
    </>
  );
};

export default AddSignersModal;
